// src/admin/ThemeManagement.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { themes } from '../theme';
import apiClient from '../services/api';
import { PageHeader, PageDescription } from '../main/LayoutStyles';
import { Card, AlertBox } from '../main/CommonStyles';
import { Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useCustomTheme } from '../theme/ThemeContext';

const ThemeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-top: 2rem;
`;

const ThemeCard = styled(Card)`
    padding: 0;
    cursor: pointer;
    border: 3px solid ${({ theme, $isActive }) => $isActive ? theme.colors.primary : 'transparent'};
    transition: all 0.2s ease-in-out;
    position: relative;
    opacity: ${({ $isSaving }) => $isSaving ? 0.5 : 1};

    &:hover {
        transform: translateY(-5px);
        box-shadow: ${({ theme }) => theme.shadows.xl};
    }
`;

const ColorPalette = styled.div`
    display: flex;
    height: 100px;
    border-radius: 0.75rem 0.75rem 0 0;
    overflow: hidden;

    & > div {
        flex: 1;
    }
`;

const CardBody = styled.div`
    padding: 1rem;
    text-align: center;
    h4 {
        font-size: 1.2rem;
        margin-bottom: 0.5rem;
    }
`;

const ActiveBadge = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: ${({ theme }) => theme.colors.secondary};
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 50px;
    font-size: 0.8rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    z-index: 2;
`;

const ThemeManagement = () => {
    const { themeName: activeTheme, setTheme } = useCustomTheme();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSelectTheme = async (themeKey) => {
        setSaving(true);
        setError('');
        try {
            await apiClient.put('/settings/active-theme', { themeName: themeKey });
            setTheme(themeKey); // Natychmiastowa zmiana motywu w całej aplikacji
        } catch (err) {
            setError("Nie udało się zapisać motywu.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <PageHeader>Zarządzanie Wyglądem Aplikacji</PageHeader>
            <PageDescription>Wybierz motyw, który będzie natychmiast widoczny dla wszystkich użytkowników aplikacji.</PageDescription>
            {error && <AlertBox type="error" style={{marginTop: '1rem'}}>{error}</AlertBox>}

            <ThemeGrid>
                {Object.entries(themes).map(([key, theme]) => {
                    const isActive = activeTheme === key;
                    return (
                        <ThemeCard key={key} $isActive={isActive} $isSaving={saving} onClick={() => !isActive && !saving && handleSelectTheme(key)}>
                            {isActive && (
                                <ActiveBadge><CheckCircle size={16}/> Aktywny</ActiveBadge>
                            )}
                            <ColorPalette>
                                <div style={{backgroundColor: theme.colors.primary}}></div>
                                <div style={{backgroundColor: theme.colors.secondary}}></div>
                                <div style={{backgroundColor: theme.colors.background}}></div>
                                <div style={{backgroundColor: theme.colors.textDark}}></div>
                            </ColorPalette>
                            <CardBody>
                                <h4>{theme.name || key}</h4>
                            </CardBody>
                        </ThemeCard>
                    );
                })}
            </ThemeGrid>
        </div>
    );
};

export default ThemeManagement;